import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {Menu, X, Droplets} from 'lucide-react'
import { NAV_LINKS, COMPANY } from '@/lib/constants'

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setIsOpen(false)
  }, [location])

  const isHome = location.pathname === '/'
  const showBg = scrolled || !isHome

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
          showBg
            ? 'bg-white/95 backdrop-blur-xl shadow-sm'
            : 'bg-transparent'
        }`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="flex items-center justify-between h-[72px]">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group" aria-label={`${COMPANY.name} home`}>
              <Droplets className={`w-5 h-5 transition-colors duration-300 ${showBg ? 'text-[#1A6B4E]' : 'text-white'}`} />
              <span className={`text-lg font-semibold font-['Outfit'] tracking-tight transition-colors duration-300 ${showBg ? 'text-[#1C1C1C]' : 'text-white'}`}>
                {COMPANY.name}
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-1">
              {NAV_LINKS.map((link) => {
                const isActive = location.pathname === link.path
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? 'text-[#1A6B4E]'
                        : `${showBg ? 'text-[#6B6B6B] hover:text-[#1C1C1C]' : 'text-white/80 hover:text-white'}`
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              })}
              <Link
                to="/contact"
                className={`ml-4 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  showBg
                    ? 'bg-[#1A6B4E] text-white hover:bg-[#134A37]'
                    : 'bg-white text-[#1C1C1C] hover:bg-white/90'
                }`}
              >
                Shop Now
              </Link>
            </div>

            {/* Mobile Toggle */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`md:hidden p-2 rounded-lg transition-colors ${showBg ? 'hover:bg-gray-100' : 'hover:bg-white/10'}`}
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isOpen}
            >
              {isOpen ? (
                <X className={`w-5 h-5 ${showBg ? 'text-[#1C1C1C]' : 'text-white'}`} />
              ) : (
                <Menu className={`w-5 h-5 ${showBg ? 'text-[#1C1C1C]' : 'text-white'}`} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 animate-fade-in">
            <div className="px-5 py-5 space-y-1">
              {NAV_LINKS.map((link) => {
                const isActive = location.pathname === link.path
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-[#E8F5EE] text-[#1A6B4E]'
                        : 'text-[#1C1C1C] hover:bg-gray-50'
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              })}
              <div className="pt-3">
                <Link to="/contact" className="btn-primary w-full justify-center text-sm">
                  Shop Now
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {isOpen && (
        <div
          className="fixed inset-0 bg-black/10 z-40 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  )
}
